/**
 * Line-style datetime icon beside each /audits row — set that log as start or end of the id range.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Check from '@mui/icons-material/Check';
import DateRange from '@mui/icons-material/DateRange';
import VerticalAlignTop from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottom from '@mui/icons-material/VerticalAlignBottom';
import * as S from './AuditsHeaderMenu.styled';

type AuditDateTimeRangeMenuProps = {
  /** Earliest source id on this row (the log itself, or combined SKU first). */
  rowStartLogId: string,
  /** Latest source id on this row. */
  rowEndLogId: string,
  rangeStartLogId?: string | null,
  rangeEndLogId?: string | null,
  /** Time range / log-id slice is active — only then show start/end on the icon. */
  rangeActive: boolean,
  startLabel: string,
  endLabel: string,
  onSetStart: () => void,
  onSetEnd: () => void,
  /** Clicking an already-set start/end unsets that bound. */
  onClearStart: () => void,
  onClearEnd: () => void,
  /** Combine SKU on — start/end is per log, not a rolled-up row. */
  disabled?: boolean,
  disabledReason?: string,
};

export const COMBINE_SKU_DATE_ICON_INFO = 'Turn off Combine SKU to set start and end date from a log.';

const AuditDateTimeRangeMenu = ({
  rowStartLogId,
  rowEndLogId,
  rangeStartLogId,
  rangeEndLogId,
  rangeActive,
  startLabel,
  endLabel,
  onSetStart,
  onSetEnd,
  onClearStart,
  onClearEnd,
  disabled = false,
  disabledReason = COMBINE_SKU_DATE_ICON_INFO,
}: AuditDateTimeRangeMenuProps) => {
  const [open, setOpen] = useState(false);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const startId = String(rowStartLogId || '').trim();
  const endId = String(rowEndLogId || '').trim();
  const isStart = Boolean(
    !disabled && rangeActive && startId && String(rangeStartLogId || '').trim() === startId,
  );
  const isEnd = Boolean(
    !disabled && rangeActive && endId && String(rangeEndLogId || '').trim() === endId,
  );

  const updatePopupPos = () => {
    const anchor = anchorRef.current;
    const popup = popupRef.current;
    if (!anchor || !popup) return;
    const rect = anchor.getBoundingClientRect();
    const popupH = popup.offsetHeight;
    const popupW = popup.offsetWidth;
    const gap = 6;
    let top = rect.bottom + gap;
    if (top + popupH > window.innerHeight - 8) {
      top = Math.max(8, rect.top - popupH - gap);
    }
    let left = rect.right - popupW;
    left = Math.max(8, Math.min(left, window.innerWidth - popupW - 8));
    setPopupPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPopupPos(null);
      return undefined;
    }
    updatePopupPos();
    const onReposition = () => updatePopupPos();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, isStart, isEnd, disabled]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (!anchorRef.current?.contains(e.target as Node)
        && !popupRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const dateTimeLabel = startLabel === endLabel
    ? startLabel
    : `${startLabel} – ${endLabel}`;

  return (
    <S.Anchor ref={anchorRef}>
      <S.DateRangeLineTrigger
        type="button"
        $open={open}
        $isStart={isStart}
        $isEnd={isEnd}
        $disabled={disabled}
        aria-label={disabled ? disabledReason : `Time range for ${dateTimeLabel}`}
        aria-expanded={open}
        aria-disabled={disabled}
        title={
          disabled
            ? disabledReason
            : isStart && isEnd
              ? `${dateTimeLabel} is start and end of time range`
              : isStart
                ? `${dateTimeLabel} is start of time range`
                : isEnd
                  ? `${dateTimeLabel} is end of time range`
                  : `Set ${dateTimeLabel} in time range`
        }
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <DateRange sx={{ fontSize: 15 }} />
      </S.DateRangeLineTrigger>
      {open && (
        <S.Popup
          ref={popupRef}
          role={disabled ? 'note' : 'menu'}
          style={popupPos
            ? { top: popupPos.top, left: popupPos.left }
            : { visibility: 'hidden' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <S.SectionLabel>Time range</S.SectionLabel>
          {disabled ? (
            <S.DisabledHint>{disabledReason}</S.DisabledHint>
          ) : (
            <S.OptionList>
              <S.OptionRow
                type="button"
                role="menuitem"
                $checked={isStart}
                onClick={() => {
                  if (isStart) onClearStart();
                  else onSetStart();
                  setOpen(false);
                }}
              >
                <S.OptionIcon>
                  <VerticalAlignTop fontSize="inherit" />
                </S.OptionIcon>
                <S.OptionText>
                  <S.OptionTitle>
                    {isStart ? 'Clear start date' : 'Set as start date'}
                  </S.OptionTitle>
                  <S.OptionHint>
                    {isStart ? `${startLabel} is the start — click to unset` : `From ${startLabel}`}
                  </S.OptionHint>
                </S.OptionText>
                <S.OptionCheck $checked={isStart}>
                  {isStart ? <Check fontSize="inherit" /> : null}
                </S.OptionCheck>
              </S.OptionRow>
              <S.OptionRow
                type="button"
                role="menuitem"
                $checked={isEnd}
                onClick={() => {
                  if (isEnd) onClearEnd();
                  else onSetEnd();
                  setOpen(false);
                }}
              >
                <S.OptionIcon>
                  <VerticalAlignBottom fontSize="inherit" />
                </S.OptionIcon>
                <S.OptionText>
                  <S.OptionTitle>
                    {isEnd ? 'Clear end date' : 'Set as end date'}
                  </S.OptionTitle>
                  <S.OptionHint>
                    {isEnd ? `${endLabel} is the end — click to unset` : `Through ${endLabel}`}
                  </S.OptionHint>
                </S.OptionText>
                <S.OptionCheck $checked={isEnd}>
                  {isEnd ? <Check fontSize="inherit" /> : null}
                </S.OptionCheck>
              </S.OptionRow>
            </S.OptionList>
          )}
        </S.Popup>
      )}
    </S.Anchor>
  );
};

export default AuditDateTimeRangeMenu;
